if (!customElements.get('bundle-shade-selector')) {
  class BundleShadeSelector extends HTMLElement {
    connectedCallback() {
      this._trigger = this.querySelector('[data-bundle-trigger]');
      this._triggerText = this.querySelector('[data-bundle-trigger-text]');
      this._triggerSwatch = this.querySelector('[data-bundle-trigger-swatch]');
      this._optionsList = this.querySelector('[data-bundle-options]');
      this._mobileQ = window.matchMedia('(max-width: 749px)');
      this._selectedVariantId = null;

      // Sheet and backdrop are siblings in the HTML; move them to <body> so
      // position:fixed is always relative to the viewport — not to any ancestor
      // that has backdrop-filter or transform (which would break fixed positioning).
      this._sheet = this.parentElement?.querySelector('[data-bvp-sheet]');
      this._backdrop = this.parentElement?.querySelector('[data-bvp-backdrop]');
      if (this._sheet) document.body.appendChild(this._sheet);
      if (this._backdrop) document.body.appendChild(this._backdrop);

      if (!this._trigger || !this._optionsList) return;

      // Trigger click: sheet on mobile, dropdown on desktop
      this._trigger.addEventListener('click', () => {
        if (this._mobileQ.matches) {
          this._openSheet();
        } else {
          this._toggleDropdown();
        }
      });

      this._trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this._mobileQ.matches) {
            this._openSheet();
          } else {
            this._toggleDropdown();
          }
        }
        if (e.key === 'Escape') this._closeDropdown();
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this._openDropdown();
          this._optionsList.querySelector('.bundle-shade-option:not([data-bvp-sheet-option])')?.focus();
        }
      });

      // Dropdown option clicks
      this._optionsList.addEventListener('click', (e) => {
        const option = e.target.closest('.bundle-shade-option');
        if (option && this._optionsList.contains(option)) {
          this._selectOption(option);
        }
      });

      this._optionsList.addEventListener('keydown', (e) => {
        const option = e.target.closest('.bundle-shade-option');
        if (e.key === 'Escape') { this._closeDropdown(); this._trigger.focus(); return; }
        if ((e.key === 'Enter' || e.key === ' ') && option) {
          e.preventDefault(); this._selectOption(option); return;
        }
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && option) {
          e.preventDefault();
          const options = [...this._optionsList.querySelectorAll('.bundle-shade-option')];
          const i = options.indexOf(option);
          const next = e.key === 'ArrowDown' ? Math.min(i + 1, options.length - 1) : Math.max(i - 1, 0);
          options[next]?.focus();
        }
      });

      // Close dropdown on outside click
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this._closeDropdown();
      });

      // Sheet interactions
      if (this._sheet) {
        this._sheet.addEventListener('click', (e) => {
          const option = e.target.closest('[data-bvp-sheet-option]');
          if (option) this._selectSheetOption(option);
        });
        this._sheet.querySelector('[data-bvp-close]')?.addEventListener('click', () => this._closeSheet());
      }

      this._backdrop?.addEventListener('click', () => this._closeSheet());

      this._onKeydown = (e) => { if (e.key === 'Escape' && this._isSheetOpen()) this._closeSheet(); };
      document.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      this._sheet?.remove();
      this._backdrop?.remove();
      document.removeEventListener('keydown', this._onKeydown);
    }

    // ─── Dropdown ───────────────────────────────────────────────────

    _isOpen() { return this._optionsList.classList.contains('is-open'); }

    _toggleDropdown() { this._isOpen() ? this._closeDropdown() : this._openDropdown(); }

    _openDropdown() {
      this._optionsList.classList.add('is-open');
      this._trigger.setAttribute('aria-expanded', 'true');
      const selected = this._optionsList.querySelector('[aria-selected="true"]');
      const first = this._optionsList.querySelector('.bundle-shade-option');
      (selected || first)?.focus();
    }

    _closeDropdown() {
      this._optionsList.classList.remove('is-open');
      this._trigger.setAttribute('aria-expanded', 'false');
    }

    _selectOption(option) {
      const { variantId, namePlain, swatchSrc } = option.dataset;
      if (!variantId) return;

      this._updateTrigger(namePlain, swatchSrc);

      this._optionsList.querySelectorAll('.bundle-shade-option').forEach((o) => o.setAttribute('aria-selected', 'false'));
      option.setAttribute('aria-selected', 'true');
      this._selectedVariantId = variantId;

      this._closeDropdown();
      this._trigger.focus();
      this._syncVariant(variantId);
    }

    // ─── Mobile sheet ────────────────────────────────────────────────

    _isSheetOpen() { return this._sheet?.classList.contains('bvp-sheet--open'); }

    _openSheet() {
      if (!this._sheet) return;
      this._sheet.removeAttribute('hidden');
      this._backdrop?.removeAttribute('hidden');
      this._sheet.setAttribute('aria-hidden', 'false');
      this._syncSheetSelection();
      requestAnimationFrame(() => {
        this._sheet.classList.add('bvp-sheet--open');
        this._backdrop?.classList.add('bvp-backdrop--visible');
      });
    }

    _closeSheet() {
      if (!this._sheet) return;
      this._sheet.classList.remove('bvp-sheet--open');
      this._backdrop?.classList.remove('bvp-backdrop--visible');
      this._sheet.setAttribute('aria-hidden', 'true');
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300;
      setTimeout(() => {
        this._sheet?.setAttribute('hidden', '');
        this._backdrop?.setAttribute('hidden', '');
      }, duration);
    }

    _syncSheetSelection() {
      this._sheet?.querySelectorAll('[data-bvp-sheet-option]').forEach((opt) => {
        opt.setAttribute('aria-selected', opt.dataset.variantId === this._selectedVariantId ? 'true' : 'false');
      });
    }

    _selectSheetOption(option) {
      const { variantId, namePlain, swatchSrc } = option.dataset;
      if (!variantId) return;

      this._updateTrigger(namePlain, swatchSrc);

      // Sync dropdown
      this._optionsList.querySelectorAll('.bundle-shade-option').forEach((o) => o.setAttribute('aria-selected', 'false'));
      this._optionsList.querySelector(`[data-variant-id="${variantId}"]`)?.setAttribute('aria-selected', 'true');

      // Sync sheet
      this._sheet.querySelectorAll('[data-bvp-sheet-option]').forEach((o) => o.setAttribute('aria-selected', 'false'));
      option.setAttribute('aria-selected', 'true');

      this._selectedVariantId = variantId;
      this._closeSheet();
      this._syncVariant(variantId);
    }

    // ─── Shared ──────────────────────────────────────────────────────

    _updateTrigger(namePlain, swatchSrc) {
      if (this._triggerText) this._triggerText.textContent = namePlain;
      if (this._triggerSwatch && swatchSrc) {
        this._triggerSwatch.src = swatchSrc;
        this._triggerSwatch.alt = namePlain;
        this._triggerSwatch.classList.add('is-visible');
      }
    }

    _syncVariant(variantId) {
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }
  }

  customElements.define('bundle-shade-selector', BundleShadeSelector);
}
